import { uniqueId } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
    ApplicationFormKeys,
    CompletePersonalInformationSchema,
    FundAllocation,
    FundAllocationsSchema,
    InvestmentPathwayOptionSchema,
} from '~/types';
import { fetchBlob, getIncomeStartDate, MAXIMUM_TAX_FREE_CASH_RATIO } from '~/utils';
import { useApplication, useApplicationContext } from './application';

const getInvestmentFund = (fund: Partial<FundAllocation>) => ({
    fundCode: fund.fundCode,
    percentage: fund?.allocation ? parseFloat(fund?.allocation) : 100,
});

export const useIllustrationInput = () => {
    const { getSession } = useApplication();
    const {
        personalInformation: { dateOfBirth, firstName, lastName, postcode },
    } = getSession(ApplicationFormKeys.PERSONAL_INFORMATION) as CompletePersonalInformationSchema;
    const {
        investmentOption,
        pensionValues: { maximumTaxFreeCash, total, withdrawals },
        withdrawalOption,
    } = useApplicationContext();
    const investmentPathwaySchema = getSession(
        ApplicationFormKeys.INVESTMENT_PATHWAY_OPTIONS
    ) as InvestmentPathwayOptionSchema;
    const fundAllocationSchema = getSession(ApplicationFormKeys.FUND_ALLOCATIONS) as FundAllocationsSchema;

    return {
        agencyCode: '5/5999',
        productType: 'SLSIPP',
        quoteType: 'NBQ',
        dependantsQuote: false,
        rounding: false,
        targetAudience: 'Customer',
        prospect: {
            firstName,
            lastName,
            dateOfBirth,
            assumedDOB: false,
            taxRate: 'basic',
            postCode: postcode,
        },
        contract: {
            taxFreeCashPercent: MAXIMUM_TAX_FREE_CASH_RATIO * 100,
            taxFreeCashAvailable: {
                amount: maximumTaxFreeCash,
                curreny: 'GBP',
            },
            currentPensionValue: {
                amount: total,
                curreny: 'GBP',
            },
        },
        investments:
            investmentOption === 'default'
                ? [getInvestmentFund(investmentPathwaySchema[ApplicationFormKeys.INVESTMENT_PATHWAY_OPTIONS])]
                : fundAllocationSchema?.fundAllocations?.map(fundAllocation => getInvestmentFund(fundAllocation)),
        lumpsumWithdrawalDetails:
            withdrawalOption !== 'Regular income'
                ? {
                      amount: withdrawals?.lumpSum || 0,
                      currency: 'GBP',
                  }
                : undefined,
        regularIncome:
            withdrawalOption !== 'Lump sum' && withdrawals?.regularIncome && withdrawals?.regularIncomePaymentDay
                ? {
                      amount: {
                          amount: withdrawals?.regularIncome,
                          curreny: 'GBP',
                      },
                      frequency: 'monthly',
                      startDate: getIncomeStartDate(withdrawals?.regularIncomePaymentDay, true),
                  }
                : undefined,
        overrideCharge: {
            fundManagementChargeGlobal: 0,
            annualAdminCharge: {
                amount: 0,
                curreny: 'GBP',
            },
        },
    };
};

export const useIllustration = () => {
    const [loading, setLoading] = useState(false);
    const [illustration, setIllustration] = useState<Blob>();
    const { current: requestId } = useRef(uniqueId('AMPP-'));
    const input = useIllustrationInput();

    const getIllustration = useCallback(async () => {
        if (loading) return;
        setLoading(true);
        try {
            const blob = await fetchBlob(
                `${import.meta.env.VITE_STANDARD_LIFE_SERVICE_ENDPOINT}/pensions/quote`,
                {
                    method: 'POST',
                    headers: {
                        channelId: 'customer-platform',
                        'x-fapi-interaction-id': requestId,
                    },
                    json: input,
                }
            );
            setIllustration(blob);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [loading, input, requestId]);

    useEffect(() => {
        if (!illustration) return;
        setLoading(false);
    }, [illustration]);

    return {
        getIllustration,
        loading,
        illustration,
    };
};
